import React from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {useDispatch} from 'react-redux';
import {Status, Statuses, useErrorModal, useIsMounted} from 'utils/hooks';
import {getIdentificaiontEventName} from './utils';
import {setCurrentEvent} from 'store/slices/identityVerificationParamSlice';
import {Location} from 'history';
import {WebcamRefTypes} from 'components/Webcam/Webcam';
import {
  LocationStates,
  PhotoLocationStateType,
} from 'views/VerifyModalScreen/VerifyModalScreen';
import {IdentityVerificationMethod} from '../../views/ConfirmIdentityGuideScreen/ConfirmIdentityGuideScreen';
import styles from './ConfirmIdentityTemplate.module.sass';
import {
  UploadPhotoComponent,
  getActivityIndicator,
  TakePhotoComponent,
} from './components';

const getLocationState = (location: Location<any>, name: LocationStates) => {
  return location.state?.[name];
};

type ConfirmIdentityTemplateProps = {
  stateName: LocationStates;
  onSubmit: (screenshot: string) => void;
  status?: Status;
  error?: string | null;
  noUpload?: boolean;
  detailsText?: string;
  preloadedScreenshot?: string;
};
const ConfirmIdentityTemplate = React.memo(
  ({
    stateName,
    onSubmit,
    status,
    preloadedScreenshot,
    noUpload = false,
    detailsText = '',
  }: ConfirmIdentityTemplateProps) => {
    const isMounted = useIsMounted();
    const history = useHistory();
    const location = useLocation<any>();
    const webcamRef = React.useRef<WebcamRefTypes>(null);
    const timeoutRef = React.useRef<any>();
    const {displayError, ErrorModal} = useErrorModal();
    const [isCaptureButtonDisabled, setIsCaptureButtonDisabled] = React.useState(true);
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const [screenshot, setScreenshot] = React.useState(preloadedScreenshot);
    const localLocationState = getLocationState(location, stateName);

    const dispatch = useDispatch();

    React.useEffect(
      function preloadPhotoFromLocationState() {
        if (localLocationState?.documentPhoto && !screenshot)
          setScreenshot(localLocationState?.documentPhoto);
        const timeout = timeoutRef.current;

        return () => {
          clearTimeout(timeout);
        };
      },
      [location.state, localLocationState, screenshot],
    );

    const enableScreenshotButton = React.useCallback(() => {
      if (isMounted.current) {
        setIsCaptureButtonDisabled(false);
      }
    }, [isMounted]);

    const isFileUpload =
      location?.state?.identityType === IdentityVerificationMethod.file;

    const captureScreenshot = React.useCallback(async () => {
      const params = {
        eventName: getIdentificaiontEventName(stateName),
        ...(stateName === 'frontSideDocumentPhoto' && {type: 'Front'}),
        ...(stateName === 'backSideDocumentPhoto' && {type: 'Back'}),
        picture: 'Taken',
      };
      try {
        const image = webcamRef?.current?.getScreenshot() || '';
        dispatch(setCurrentEvent(params));
        setScreenshot(image);
      } catch (err) {
        displayError(err);
      }
    }, [dispatch, displayError, stateName]);

    const onRetry = React.useCallback(() => {
      setScreenshot('');
      const newLocalstate: PhotoLocationStateType = {
        ...localLocationState,
        documentPhoto: '',
      };
      const newState = {...location.state, [stateName]: newLocalstate};
      history.replace(location.pathname, newState);
    }, [history, localLocationState, location.pathname, location.state, stateName]);

    const onSend = React.useCallback(
      (screenshot: string) => {
        setScreenshot('');
        onSubmit(screenshot);
      },
      [onSubmit],
    );

    const isDisabled =
      status === Statuses.loading ||
      status === Statuses.success ||
      status === Statuses.error;

    const activityIndicator = React.useMemo(() => getActivityIndicator(status), [status]);

    const props = React.useMemo(
      () => ({
        screenshot,
        webcamRef,
        isCaptureButtonDisabled,
        activityIndicator,
        onRetry,
        disabled: isDisabled,
        setScreenshot,
        validationError,
        setValidationError,
        onUserMedia: enableScreenshotButton,
        onSend,
        onCapture: captureScreenshot,
        stateName,
        minScreenshotW: 800,
        minScreenshotH: 600,
      }),
      [
        activityIndicator,
        captureScreenshot,
        enableScreenshotButton,
        isCaptureButtonDisabled,
        validationError,
        isDisabled,
        onRetry,
        onSend,
        screenshot,
        stateName,
      ],
    );

    return (
      <>
        <ErrorModal />
        <div className={styles.content}>
          <div className={styles.details}>{detailsText}</div>
          <div className={styles.validationErrorMessage}>{validationError}</div>
          {isFileUpload && !noUpload ? (
            <UploadPhotoComponent {...props} />
          ) : (
            <TakePhotoComponent {...props} />
          )}
        </div>
      </>
    );
  },
);

export {ConfirmIdentityTemplate};
